import { useEffect, useState } from 'react';
import { FreeMode } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import '../styles/LogoList.css';
import { Logo } from './tablet/Logo';
import { DeleteLogoConfirmPopup } from '../DeleteLogoConfirmPopup';

export function LogoList({
  logos,
  customerId,
  title,
  subTitle,
  handleAddNewLogo,
  activeLogoIndex,
  handleActiveLogoIndex,
  handleDeleteLogo,
  viewMode,
}) {
  const isCustomerMode = viewMode === 'customer';
  const [deletePopupState, setDeletePopupState] = useState({
    isOpen: false,
    logo: null,
    isLoading: false,
  });
  // State to force re-render when window.decals changes
  const [, setDecalsUpdate] = useState(0);

  const handleDeleteClick = (logo) => {
    setDeletePopupState({
      isOpen: true,
      logo: logo,
    });
  };

  const handleConfirmDelete = async () => {
    if (deletePopupState.logo && handleDeleteLogo) {
      setDeletePopupState((prev) => ({ ...prev, isLoading: true }));
      try {
        await handleDeleteLogo(deletePopupState.logo);
        setDeletePopupState({
          isOpen: false,
          logo: null,
          isLoading: false,
        });
      } catch (error) {
        setDeletePopupState((prev) => ({ ...prev, isLoading: false }));
      }
    }
  };

  const handleCancelDelete = () => {
    setDeletePopupState({
      isOpen: false,
      logo: null,
    });
  };

  // Monitor window.decals changes to trigger re-render when logos are added/removed from model
  useEffect(() => {
    let lastDecalsLength = window.decals?.length || 0;
    let lastDecalsIds =
      window.decals
        ?.map((d) => (d.logo && d.logo.Id ? d.logo.Id : null))
        .filter((id) => id !== null)
        .sort()
        .join(',') || '';

    const checkDecals = () => {
      const currentLength = window.decals?.length || 0;
      const currentIds =
        window.decals
          ?.map((d) => (d.logo && d.logo.Id ? d.logo.Id : null))
          .filter((id) => id !== null)
          .sort()
          .join(',') || '';

      // Only update state if decals actually changed (length or IDs)
      if (currentLength !== lastDecalsLength || currentIds !== lastDecalsIds) {
        lastDecalsLength = currentLength;
        lastDecalsIds = currentIds;
        setDecalsUpdate((prev) => prev + 1);
      }
    };

    // Check periodically for changes in window.decals
    // Increased interval to 200ms to reduce performance impact
    const interval = setInterval(checkDecals, 200);

    return () => {
      clearInterval(interval);
    };
  }, []);

  return (
    <div id={customerId}>
      <div
        style={{
          position: 'relative',
          padding: '0px 8px',
          height: '100%',
        }}
      >
        <Swiper
          slidesPerView="auto"
          spaceBetween={10}
          freeMode={true}
          modules={[FreeMode]}
          className="logo-list-swiper"
        >
          {!isCustomerMode && (
            <SwiperSlide key="key" className="custom-slide">
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <div className="addLogo">
                  <img src="/theme/img/plusIcon.svg" alt="plusIcon" />
                  <input
                    className="fullOwner_opacity"
                    type="file"
                    name="myfile"
                    accept="image/*"
                    style={{ width: '100%', height: '100%' }}
                    onChange={(e) => handleAddNewLogo(e)}
                  />
                </div>
              </div>
            </SwiperSlide>
          )}
          {logos?.map((model, index) => {
            // Check if logo is currently on the model (exists in window.decals)
            const isLogoOnModel =
              window.decals &&
              window.decals.some(
                (d) =>
                  d.logo &&
                  d.logo.Id === model.Id &&
                  d.logo.IsText !== true &&
                  d.logo.IsPattern !== true,
              );

            // Hide logo if it's already on the model
            if (isLogoOnModel) {
              return null;
            }

            const isNew = model.Position === null;
            const isUserUploadedLogo = model.isUserUploadedLogo === true;
            return (
              <SwiperSlide key={index} className="custom-slide">
                <Logo
                  scale={model.Scale}
                  rotate={model.Rotate}
                  image={model.Thumbnail}
                  onClick={() => handleActiveLogoIndex(index)}
                  active={activeLogoIndex === index}
                  isNew={isNew}
                  title={title}
                  subTitle={subTitle}
                  isUserUploadedLogo={isUserUploadedLogo}
                  onDelete={() => handleDeleteClick(model)}
                />
              </SwiperSlide>
            );
          })}
        </Swiper>
      </div>
      <DeleteLogoConfirmPopup
        isOpen={deletePopupState.isOpen}
        logo={deletePopupState.logo}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        isLoading={deletePopupState.isLoading}
      />
    </div>
  );
}
